import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Fuel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errMsg, fmt, fmtDate, PAYMENTS, todayStr } from "@/lib/api";
import { usePanel } from "./PanelContext";
import { PaymentPicker } from "./NewOrderDialog";

const shift = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toLocaleDateString("sv-SE"); };
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("sv-SE"); };

function ExpenseDialog({ open, onOpenChange, initial, cats, onSaved }) {
  const { api } = usePanel();
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(initial || { category: "Yakıt", amount: "", note: "", date: todayStr(), payment_method: "nakit" }); }, [open, initial]);
  const save = async () => {
    if (!f.amount || parseFloat(f.amount) <= 0) return toast.error("Tutar girin");
    const body = { category: f.category, amount: parseFloat(f.amount), note: f.note, date: f.date, payment_method: f.payment_method };
    try {
      if (initial) await api.put(`/expenses/${initial.id}`, body); else await api.post("/expenses", body);
      toast.success("Gider kaydedildi"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-dialog">
        <DialogHeader><DialogTitle>{initial ? "Gideri Düzenle" : "Gider Ekle"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Kategori</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c} data-testid={`expense-category-${c}`}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Tutar (TL)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="expense-amount-input" /></div>
          <div><Label>Tarih</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="expense-date-input" /></div>
          <div><Label>Açıklama</Label><Input value={f.note} placeholder="ör. Motor benzini" onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="expense-note-input" /></div>
          <div className="col-span-2"><Label>Ödeme şekli</Label><PaymentPicker value={f.payment_method} onChange={(v) => setF({ ...f, payment_method: v })} prefix="expense-payment" /></div>
        </div>
        <Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={save} data-testid="expense-save-btn">Kaydet</Button>
      </DialogContent>
    </Dialog>
  );
}

const Card = ({ label, value, testId, tone = "" }) => (
  <div className={`rounded-3xl border p-5 ${tone || "bg-white"}`} data-testid={testId}>
    <p className="text-sm opacity-70">{label}</p><p className="mt-2 font-display text-2xl font-extrabold font-mono-num">{fmt(value)}</p>
  </div>
);

export default function Kasa() {
  const { api } = usePanel();
  const [range, setRange] = useState({ start: todayStr(), end: todayStr() });
  const [sum, setSum] = useState(null);
  const [exps, setExps] = useState([]);
  const [cats, setCats] = useState([]);
  const [dlg, setDlg] = useState({ open: false, item: null });

  const load = () => Promise.all([api.get("/kasa/summary", { params: range }), api.get("/expenses", { params: range })])
    .then(([s, e]) => { setSum(s.data); setExps(e.data); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/expense-categories").then((r) => setCats(r.data)); }, [api]);
  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (x) => { if (window.confirm("Gider silinsin mi?")) { await api.delete(`/expenses/${x.id}`); load(); } };
  const quick = [["Bugün", todayStr(), todayStr()], ["Son 7 gün", shift(-6), todayStr()], ["Bu ay", monthStart(), todayStr()]];

  return (
    <div className="space-y-6 rise" data-testid="kasa-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto"><h1 className="text-3xl font-extrabold">Kasa & Giderler</h1><p className="text-sm text-slate-500">Teslim edilen siparişler gelire, eklediğiniz giderler kasadan düşer.</p></div>
        <Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] gap-1" onClick={() => setDlg({ open: true, item: null })} data-testid="kasa-add-expense-btn"><Plus className="h-4 w-4" />Gider Ekle</Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {quick.map(([l, s, e]) => <Button key={l} size="sm" variant={range.start === s && range.end === e ? "default" : "outline"} className="rounded-full" onClick={() => setRange({ start: s, end: e })} data-testid={`kasa-range-${l}`}>{l}</Button>)}
        <Input type="date" className="w-40" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="kasa-start-input" />
        <span>—</span>
        <Input type="date" className="w-40" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="kasa-end-input" />
      </div>
      {sum && (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card label="Toplam Gelir" value={sum.total_income} testId="kasa-total-income" tone="bg-[#0F1F45] text-white border-[#0F1F45]" />
            <Card label="Toplam Gider" value={sum.total_expense} testId="kasa-total-expense" tone="bg-red-50 text-red-900 border-red-100" />
            <Card label="Net Kâr" value={sum.net} testId="kasa-net" tone="bg-emerald-50 text-emerald-900 border-emerald-100" />
            <Card label="Kasadaki Nakit" value={sum.cash_in_drawer} testId="kasa-cash-drawer" tone="bg-orange-50 text-orange-900 border-orange-100" />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(PAYMENTS).map(([k, l]) => <Card key={k} label={`${l} Geliri`} value={sum.income[k]} testId={`kasa-income-${k}`} />)}
          </div>
          <div className="rounded-3xl bg-white border p-5">
            <h3 className="font-display font-bold mb-3">Kategoriye Göre Giderler</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(sum.expenses_by_category).map(([c, v]) => <span key={c} className="rounded-full bg-slate-100 px-4 py-2 text-sm"><b>{c}</b> {fmt(v)}</span>)}
              {!Object.keys(sum.expenses_by_category).length && <p className="text-sm text-slate-500">Bu aralıkta gider yok.</p>}
            </div>
          </div>
        </>
      )}
      <div className="rounded-3xl bg-white border overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Tarih</TableHead><TableHead>Kategori</TableHead><TableHead>Açıklama</TableHead><TableHead>Ödeme</TableHead><TableHead className="text-right">Tutar</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {exps.map((x) => (
              <TableRow key={x.id} data-testid={`expense-row-${x.id}`}>
                <TableCell>{fmtDate(x.date)}</TableCell>
                <TableCell><span className="inline-flex items-center gap-1 font-semibold">{x.category === "Yakıt" && <Fuel className="h-3.5 w-3.5 text-[#FF6B0A]" />}{x.category}</span></TableCell>
                <TableCell className="text-slate-600">{x.note}</TableCell>
                <TableCell>{PAYMENTS[x.payment_method] || "-"}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{fmt(x.amount)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => setDlg({ open: true, item: x })} data-testid={`expense-edit-btn-${x.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(x)} data-testid={`expense-delete-btn-${x.id}`}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!exps.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Gider kaydı yok.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <ExpenseDialog open={dlg.open} initial={dlg.item} cats={cats} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={load} />
    </div>
  );
}
